/**
 * The feedback panel — a popover anchored above the floating feedback button.
 *
 * Owns the form state and the submission state machine (idle → submitting →
 * success/error). Deliberately a non-dimming popover rather than a modal dialog
 * so the search results stay visible while the user describes their problem.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { FEEDBACK_CATEGORY_META } from './feedbackCategories';
import { FeedbackForm } from './FeedbackForm';
import type { FeedbackFormValue } from './FeedbackForm';
import { isValidEmail } from './feedbackValidation';
import { apiService, ApiServiceError } from '../../services';
import { useAuth } from '../../auth';
import { config, resolveRuntimeValue } from '../../config';
import { getSelectedLanguage } from '../LanguageSelector';
import type { FeedbackConfig, FeedbackResponse } from '../../types/feedback';

const EMPTY_FORM: FeedbackFormValue = {
  category: 'issue',
  summary: '',
  details: '',
  email: '',
  files: [],
};

/** How long the confirmation stays on screen before the panel closes itself. */
const SUCCESS_AUTO_CLOSE_MS = 3000;

/** Used when no VITE_FEEDBACK_PORTAL_URL is configured for the environment. */
const DEFAULT_PORTAL_URL = 'https://selfservice.copadata.com/portal/en/newticket?departmentId=57961000000007061&layoutId=57961000000023011';

type Status = 'idle' | 'submitting' | 'success' | 'error';

interface FeedbackPanelProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  feedbackConfig: FeedbackConfig;
  /** Active search query, sent along as context for the support agent. */
  searchQuery?: string;
}

function isComplete(value: FeedbackFormValue): boolean {
  if (!isValidEmail(value.email)) return false;
  const summary = value.summary.trim();
  const details = value.details.trim();
  return FEEDBACK_CATEGORY_META[value.category].fieldsRequired
    ? Boolean(summary && details)
    : Boolean(summary || details);
}

export function FeedbackPanel({
  open,
  anchorEl,
  onClose,
  feedbackConfig,
  searchQuery,
}: FeedbackPanelProps) {
  const { t } = useTranslation('feedback');
  const { user } = useAuth();
  const defaultEmail = user?.email ?? '';
  const [value, setValue] = useState<FeedbackFormValue>(EMPTY_FORM);
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [result, setResult] = useState<FeedbackResponse | null>(null);
  const closeTimer = useRef<number | undefined>(undefined);

  const portalUrl = resolveRuntimeValue(config.feedback.portalUrl) ?? DEFAULT_PORTAL_URL;
  const isDirty =
    value.summary.trim() !== '' || value.details.trim() !== '' || value.files.length > 0;

  const reset = useCallback(() => {
    setValue(EMPTY_FORM);
    setStatus('idle');
    setErrorMessage(null);
    setResult(null);
  }, []);

  // Clear the form once the closing animation has finished, so the user never
  // sees the content disappear while the panel is still on screen.
  const handleExited = useCallback(() => {
    reset();
  }, [reset]);

  // Prefill on open rather than on mount: the OIDC session may still be loading.
  useEffect(() => {
    if (open) setValue((prev) => (prev.email ? prev : { ...prev, email: defaultEmail }));
  }, [defaultEmail, open]);

  const handleClose = useCallback(
    (_event: object, reason?: 'backdropClick' | 'escapeKeyDown') => {
      if (status === 'submitting') return;
      // Guard against losing a half-written report to a stray click.
      if (reason === 'backdropClick' && isDirty && status === 'idle') {
        if (!window.confirm(t('discardConfirm'))) return;
      }
      onClose();
    },
    [isDirty, onClose, status, t]
  );

  const handleSubmit = useCallback(async () => {
    setStatus('submitting');
    setErrorMessage(null);

    try {
      const response = await apiService.submitFeedback({
        category: value.category,
        summary: value.summary.trim(),
        details: value.details.trim(),
        pageUrl: window.location.href,
        searchQuery,
        uiLanguage: getSelectedLanguage(),
        email: value.email.trim() || undefined,
        files: value.files,
      });
      setResult(response);
      setStatus('success');
    } catch (error) {
      const rateLimited = error instanceof ApiServiceError && error.status === 429;
      setErrorMessage(t(rateLimited ? 'errors.rateLimited' : 'errors.submitFailed'));
      setStatus('error');
    }
  }, [searchQuery, t, value]);

  // Auto-close after a successful submission.
  useEffect(() => {
    if (status !== 'success') return;
    closeTimer.current = window.setTimeout(onClose, SUCCESS_AUTO_CLOSE_MS);
    return () => window.clearTimeout(closeTimer.current);
  }, [onClose, status]);

  const isSubmitting = status === 'submitting';
  const meta = FEEDBACK_CATEGORY_META[value.category];

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      marginThreshold={8}
      slotProps={{
        transition: { onExited: handleExited },
        paper: {
          elevation: 6,
          sx: {
            // Sits directly above the FAB, right-aligned with it.
            mt: -1.5,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxHeight: 'calc(100vh - 120px)',
            overflowY: 'auto',
            borderRadius: 3,
          },
        },
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1" component="h2" fontWeight={600}>
            {t('title')}
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleClose({})}
            disabled={isSubmitting}
            aria-label={t('close')}
            sx={{ mr: -0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {status === 'success' ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              py: 4,
              textAlign: 'center',
            }}
            role="status"
          >
            <CheckCircleOutlineIcon color="success" fontSize="large" />
            <Typography variant="subtitle1">{t('success.title')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('success.message')}
            </Typography>
            {result?.ticket_number && (
              <Typography variant="body2" color="text.secondary">
                {t('success.ticket', { number: result.ticket_number })}
              </Typography>
            )}
            {Boolean(result?.attachments_failed) && (
              <Typography variant="caption" color="warning.main">
                {t('success.attachmentsFailed', { count: result?.attachments_failed })}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            <FeedbackForm
              value={value}
              onChange={setValue}
              config={feedbackConfig}
              disabled={isSubmitting}
            />

            {errorMessage && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={!isComplete(value) || isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{ mt: 2 }}
            >
              {t(meta.submitLabelKey)}
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 1.5, textAlign: 'center' }}
            >
              <Trans
                t={t}
                i18nKey="footer.text"
                components={[
                  <Link
                    key="portal"
                    href={portalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  />,
                ]}
              />
            </Typography>
          </>
        )}
      </Box>
    </Popover>
  );
}
